import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPublicRoom, joinPublicRoom, obtenerJuegos } from "../api";
import { useUser } from "../context/AuthContext";
import ParticleBackground from "../components/ParticleBackground";
import {
  UserGroupIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const JoinPublicRoom = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser?.() || {};
  const publicRoomId = new URLSearchParams(location.search).get("publicRoomId");
  const [publicRoom, setPublicRoom] = useState(null);
  const [gameName, setGameName] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    players: 1,
    nombre_cliente: "",
    email: "",
    telefono: "",
  });

  // Calcular precio por jugador y total
  const precioPorJugador = publicRoom ? Number(publicRoom.precio_por_jugador) : 0;
  const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
  const total = precioPorJugador * Number(formData.players || 1);

  // Parse date/time from publicRoom
  let initialDate = "";
  let initialStart = "";
  let initialEnd = "";
  if (publicRoom) {
    const dt = new Date(publicRoom.start_time);
    initialDate = dt.toISOString().slice(0, 10);
    initialStart = dt.toTimeString().slice(0, 5);
    if (publicRoom.end_time) {
      const dtEnd = new Date(publicRoom.end_time);
      initialEnd = dtEnd.toTimeString().slice(0, 5);
    }
  }

  useEffect(() => {
    if (!publicRoomId) return;
    const fetchRoom = async () => {
      try {
        const room = await getPublicRoom(publicRoomId);
        setPublicRoom(room);
      } catch (e) {
        setPublicRoom(null);
      }
    };
    fetchRoom();
  }, [publicRoomId]);

  // After room loads, fetch game name from public games endpoint
  useEffect(() => {
    const fetchGameName = async () => {
      if (!publicRoom?.game_id) return;
      try {
        const juegos = await obtenerJuegos();
        const g = juegos.find((j) => String(j.id) === String(publicRoom.game_id));
        setGameName(g?.nombre || `Game #${publicRoom.game_id}`);
      } catch (err) {
        setGameName(`Game #${publicRoom.game_id}`);
      }
    };
    fetchGameName();
  }, [publicRoom?.game_id]);

  // Prefill with user profile if available
  useEffect(() => {
    if (isLoggedIn && user) {
      setFormData((prev) => ({
        ...prev,
        nombre_cliente: user.name || prev.nombre_cliente,
        email: user.email || prev.email,
        telefono: user.phone || prev.telefono,
      }));
    }
  }, [isLoggedIn, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = [];
    const maxSpots = (publicRoom?.capacidad_maxima || 0) - (publicRoom?.jugadores_actuales || 0);
    const players = parseInt(formData.players || 0);
    const emailRegex = /.+@.+\..+/;

    if (!players || players < 1) errors.push("Players must be at least 1.");
    if (players > maxSpots) errors.push(`Only ${maxSpots} spots available.`);
    if (!formData.nombre_cliente?.trim()) errors.push("Full Name is required.");
    if (!formData.email?.trim() || !emailRegex.test(formData.email)) errors.push("A valid email is required.");
    if (!formData.telefono?.trim()) errors.push("Phone is required.");

    if (errors.length) {
      setMensaje(`❌ Please fix the following:\n- ${errors.join("\n- ")}`);
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    try {
      await joinPublicRoom({
        public_room_id: publicRoom.id,
        user_id: isLoggedIn && user?.id ? user.id : null,
        cantidad_jugadores: players,
        nombre_cliente: formData.nombre_cliente,
        email: formData.email,
        telefono: formData.telefono,
      });
      const dtStart = new Date(publicRoom.start_time);
      const dtEnd = publicRoom.end_time ? new Date(publicRoom.end_time) : null;
      navigate('/thanks', {
        state: {
          game_id: publicRoom.game_id,
          gameName,
          date: dtStart.toISOString().slice(0,10),
          start: dtStart.toTimeString().slice(0,5),
          end: dtEnd ? dtEnd.toTimeString().slice(0,5) : '',
          players,
          nombre_cliente: formData.nombre_cliente,
          email: formData.email,
          telefono: formData.telefono,
          precio_por_jugador: precioPorJugador,
          type: 'public',
        }
      });
      return;
    } catch (err) {
      setMensaje("❌ Error joining the public room. Please try again later.");
    } finally {
      setShowModal(true);
      setIsLoading(false);
    }
  };

  if (!publicRoom)
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-2xl text-purple-400 font-audiowide animate-pulse">
          Loading public room...
        </div>
      </div>
    );

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 font-inter py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <ParticleBackground />
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex flex-col items-center text-center">
              {mensaje.includes("✅") ? (
                <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
              ) : (
                <XCircleIcon className="h-16 w-16 text-red-500 mb-4" />
              )}
              <h3 className="text-xl font-audiowide mb-4">
                {mensaje.includes("✅")
                  ? "Joined Public Room!"
                  : "Error"}
              </h3>
              <p className="text-gray-300 mb-6">{mensaje}</p>
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="relative max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-audiowide text-center mb-10 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Join Public Room #{publicRoom.id}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 bg-gray-800/60 rounded-2xl border border-gray-700">
              <h2 className="text-xl font-semibold text-purple-300 mb-4">Room Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-gray-200">
                <div>
                  <p className="text-sm text-gray-400">Game</p>
                  <p className="font-medium">{gameName || `Game #${publicRoom.game_id}`}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Date</p>
                  <p className="font-medium">{initialDate}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Start</p>
                  <p className="font-medium">{initialStart}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">End</p>
                  <p className="font-medium">{initialEnd}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Available</p>
                  <p className="font-medium">{publicRoom.capacidad_maxima - publicRoom.jugadores_actuales}</p>
                </div>
              </div>
            </div>

            <form id="joinPublicRoomForm" onSubmit={handleSubmit} className="space-y-8 bg-gray-800/50 rounded-2xl p-8 shadow-xl" noValidate>
              {/* Players Section */}
              <div>
                <h3 className="text-lg font-semibold text-purple-300 mb-3">Players</h3>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Players</label>
                  <div className="relative">
                    <UserGroupIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                      name="players"
                      min={1}
                      max={publicRoom.capacidad_maxima - publicRoom.jugadores_actuales}
                      value={formData.players}
                      onChange={handleChange}
                      required
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-400">
                    {publicRoom.capacidad_maxima - publicRoom.jugadores_actuales === 0
                      ? "No spots available"
                      : `Available spots: ${publicRoom.capacidad_maxima - publicRoom.jugadores_actuales}`}
                  </p>
                </div>
              </div>

              {/* Contact Information Section */}
              <div>
                <h3 className="text-lg font-semibold text-purple-300 mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                    <div className="relative">
                      <UserIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                      <input
                        type="text"
                        name="nombre_cliente"
                        value={formData.nombre_cliente}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <div className="relative">
                      <EnvelopeIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="relative md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                    <div className="relative">
                      <PhoneIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="bg-gray-800/50 rounded-2xl p-8 shadow-xl border-l-4 border-purple-500">
              <h2 className="text-3xl font-audiowide mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Reservation Summary
              </h2>
              <div className="space-y-4 text-gray-300">
                <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                  <span className="font-medium">Price per player:</span>
                  <span className="text-green-400 font-mono">{usd(precioPorJugador)}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                  <span className="font-medium">Players:</span>
                  <span className="text-purple-400">{formData.players}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                  <span className="font-medium">Time:</span>
                  <span className="text-gray-100 font-mono">{initialStart}{initialEnd ? ` - ${initialEnd}` : ''}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                  <span className="font-medium">Visibility:</span>
                  <span className="text-green-400">Public</span>
                </div>
                <div className="flex justify-between items-center pt-4">
                  <span className="text-xl font-bold">Total:</span>
                  <span className="text-2xl font-audiowide text-green-400">{usd(total)}</span>
                </div>
              </div>
              <p className="mt-3 text-sm text-blue-300">You are joining a public room</p>
              <div className="mt-4 p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <p className="text-sm text-yellow-100">
                    Payment is collected at the venue on the day of your reservation. Please arrive <span className="font-semibold">10 minutes early</span> for check-in and briefing.
                  </p>
                </div>
              </div>
              <button
                type="submit"
                form="joinPublicRoomForm"
                disabled={isLoading || (publicRoom.capacidad_maxima - publicRoom.jugadores_actuales) === 0}
                className="mt-5 w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 rounded-lg font-audiowide text-lg transition-all relative disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading
                  ? "Joining..."
                  : (publicRoom.capacidad_maxima - publicRoom.jugadores_actuales) === 0
                  ? "No spots available"
                  : "Join Public Room"}
              </button>
            </div>
          </aside>
        </div>

        <div role="separator" aria-label="Section divider" className="my-10 h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent"></div>

        {/* Public Room Info - Bottom Section */}
        <div className="mt-8">
          <div className="p-6 bg-gray-800/60 rounded-2xl border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <InformationCircleIcon className="h-6 w-6 text-blue-400" />
              <h3 className="text-lg md:text-xl font-audiowide text-gray-100">What does a public room mean?</h3>
            </div>
            <ul className="list-disc pl-5 text-gray-300 space-y-2">
              <li>Your room is visible to other players for the same date and time.</li>
              <li>Other players can join until the room reaches its maximum capacity.</li>
              <li>You’ll share the arena with other guests (mixed group experience).</li>
              <li>Pricing is per player; your total reflects the number of players you bring.</li>
              <li>Please arrive 10 minutes early for briefing and gear setup.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinPublicRoom;
